import * as React from "react"

import { cn } from "@/utils"

const Skeleton = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    /** Width — accepts any CSS value or number (px) */
    width?: string | number
    /** Height — accepts any CSS value or number (px) */
    height?: string | number
    /** Render as circle */
    circle?: boolean
    /** Number of skeleton rows to render */
    count?: number
    /** Show active pulse animation (default true) */
    active?: boolean
  }
>(
  (
    { className, width, height, circle, count, active = true, style, ...props },
    ref
  ) => {
    const baseStyle: React.CSSProperties = {
      ...style,
      width: typeof width === "number" ? `${width}px` : width,
      height: typeof height === "number" ? `${height}px` : height,
    }

    const el = (
      <div
        ref={ref}
        className={cn(
          "bg-muted rounded-md",
          active && "animate-pulse",
          circle && "rounded-full",
          className
        )}
        style={baseStyle}
        {...props}
      />
    )

    if (count && count > 1) {
      return (
        <div className="flex flex-col gap-2">
          {Array.from({ length: count }).map((_, i) => (
            <div
              key={i}
              className={cn(
                "bg-muted rounded-md",
                active && "animate-pulse",
                circle && "rounded-full",
                className
              )}
              style={baseStyle}
              {...props}
            />
          ))}
        </div>
      )
    }

    return el
  }
)
Skeleton.displayName = "Skeleton"

/** Paragraph-shaped skeleton block — matches antd Skeleton.Paragraph */
function SkeletonParagraph({
  rows = 3,
  className,
}: {
  rows?: number
  className?: string
}) {
  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton
          key={i}
          className="h-4"
          style={{ width: i === rows - 1 ? "60%" : "100%" }}
        />
      ))}
    </div>
  )
}

/** Avatar-shaped skeleton */
function SkeletonAvatar({
  size = 40,
  className,
}: {
  size?: number
  className?: string
}) {
  return <Skeleton circle width={size} height={size} className={className} />
}

/** Button-shaped skeleton */
function SkeletonButton({ className }: { className?: string }) {
  return <Skeleton className={cn("h-10 w-24 rounded-md", className)} />
}

/** Input-shaped skeleton */
function SkeletonInput({ className }: { className?: string }) {
  return <Skeleton className={cn("h-10 w-full rounded-md", className)} />
}

export { Skeleton, SkeletonParagraph, SkeletonAvatar, SkeletonButton, SkeletonInput }
